import type { DataTableCell, DataTableColumn, DataTableColumnFormat } from './types/views.ts';

/**
 * U2 (02/11/2026): how a value of an app list reads. The column's declared
 * format wins (the step that wrote it knows); a role says it too (a domain is a
 * link); otherwise the code reads the shape every value shares. Words never
 * decide a format or a color: only the declaration or the shape.
 */

export type CellFormat = DataTableColumnFormat | 'link' | 'email';

/** A value longer than this, in most objects, is a paragraph: it belongs to the object's page. */
const LONG_TEXT = 90;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;
const NUMBER = /^-?\d[\d\s  ]*([.,]\d+)?$/;
const RANGE = /^(-?\d[\d\s  ]*)\s*(?:-|–|à)\s*(-?\d[\d\s  ]*)$/;
const RATING = /^(\d+(?:[.,]\d+)?)\s*\/\s*(\d+(?:[.,]\d+)?)$/;
const PERCENT = /^(-?\d+(?:[.,]\d+)?)\s*%$/;
const MONEY = /^(-?\d[\d\s  ]*(?:[.,]\d+)?)\s*(k€|M€|€|EUR|\$|USD|£|GBP)$/i;
const URL = /^https?:\/\/\S+$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const cellString = (cell: DataTableCell | undefined): string => {
  const value = cell?.value;
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return String(value).trim();
};

const shapeOf = (values: string[]): CellFormat => {
  if (values.length === 0) return 'text';
  const all = (pattern: RegExp) => values.every((value) => pattern.test(value));
  if (all(ISO_DATE)) return 'date';
  if (all(URL)) return 'link';
  if (all(EMAIL)) return 'email';
  if (values.every((value) => NUMBER.test(value) || RANGE.test(value))) return 'number';
  if (all(PERCENT)) return 'percent';
  if (all(MONEY)) return 'money';
  if (all(RATING)) return 'rating';
  const long = values.filter((value) => value.length > LONG_TEXT).length;
  return long * 2 > values.length ? 'long_text' : 'text';
};

/** The format of a column, read once over the values of every object. */
export const columnFormat = (column: DataTableColumn, values: Array<DataTableCell | undefined>): CellFormat => {
  if (column.format) return column.format;
  if (column.role === 'domain' || column.role === 'url') return 'link';
  if (column.role === 'email') return 'email';
  if (column.role === 'name' || column.role === 'company' || column.role === 'crm_id' || column.role === 'event_id') return 'text';
  return shapeOf(values.map(cellString).filter(Boolean));
};

const toNumber = (raw: string): number | null => {
  const value = Number(raw.replace(/[\s  ]/g, '').replace(',', '.'));
  return Number.isFinite(value) ? value : null;
};

const fr = (value: number, digits = 2) => value.toLocaleString('fr-FR', { maximumFractionDigits: digits });

const DAY = 86_400_000;
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** « aujourd'hui », « il y a 3 j », « dans 5 j », else the day itself. */
export const relativeDay = (iso: string, now: Date = new Date()): { relative: string; exact: string } | null => {
  const date = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(date.getTime())) return null;
  const exact = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const diff = Math.round((startOfDay(date) - startOfDay(now)) / DAY);
  if (diff === 0) return { relative: 'aujourd’hui', exact };
  if (diff === -1) return { relative: 'hier', exact };
  if (diff === 1) return { relative: 'demain', exact };
  if (diff < 0 && diff >= -45) return { relative: `il y a ${-diff} j`, exact };
  if (diff > 0 && diff <= 45) return { relative: `dans ${diff} j`, exact };
  const short = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) });
  return { relative: short, exact };
};

/** What a cell shows, already formatted; the screen only draws it. */
export type CellDisplay =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'long_text'; text: string }
  | { kind: 'number'; text: string }
  | { kind: 'date'; text: string; exact: string }
  | { kind: 'tags'; items: string[] }
  | { kind: 'rating'; value: number; max: number; text: string }
  | { kind: 'decision'; text: string }
  | { kind: 'link'; href: string; text: string }
  | { kind: 'email'; href: string; text: string };

const linkOf = (raw: string): CellDisplay => {
  const href = URL.test(raw) ? raw : /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(raw) ? `https://${raw}` : null;
  if (!href) return { kind: 'text', text: raw };
  try {
    const url = new globalThis.URL(href);
    const path = url.pathname !== '/' ? url.pathname : '';
    return { kind: 'link', href, text: `${url.hostname.replace(/^www\./, '')}${path}` };
  } catch {
    return { kind: 'text', text: raw };
  }
};

/** A value as its format reads it; a value that does not fit its format is shown as written. */
export const displayCell = (format: CellFormat, cell: DataTableCell | undefined, now: Date = new Date()): CellDisplay => {
  const raw = cellString(cell);
  if (!raw) return { kind: 'empty' };
  switch (format) {
    case 'number': {
      const range = RANGE.exec(raw);
      if (range) {
        const [low, high] = [toNumber(range[1]!), toNumber(range[2]!)];
        if (low !== null && high !== null) return { kind: 'number', text: `${fr(low)}–${fr(high)}` };
      }
      const value = NUMBER.test(raw) ? toNumber(raw) : null;
      return value === null ? { kind: 'text', text: raw } : { kind: 'number', text: fr(value) };
    }
    case 'percent': {
      const match = PERCENT.exec(raw) ?? (NUMBER.test(raw) ? [raw, raw] : null);
      const value = match ? toNumber(match[1]!) : null;
      return value === null ? { kind: 'text', text: raw } : { kind: 'number', text: `${fr(value, 1)} %` };
    }
    case 'money': {
      const match = MONEY.exec(raw);
      const value = match ? toNumber(match[1]!) : NUMBER.test(raw) ? toNumber(raw) : null;
      if (value === null) return { kind: 'text', text: raw };
      const unit = match?.[2] ?? '€';
      const symbol = /^eur$/i.test(unit) ? '€' : /^usd$/i.test(unit) ? '$' : /^gbp$/i.test(unit) ? '£' : unit;
      return { kind: 'number', text: `${fr(value, 0)} ${symbol}` };
    }
    case 'date': {
      const day = ISO_DATE.test(raw) ? relativeDay(raw, now) : null;
      return day ? { kind: 'date', text: day.relative, exact: day.exact } : { kind: 'text', text: raw };
    }
    case 'tags': {
      const items = raw.split(/\s*[,;·|]\s*|\n+/).map((item) => item.trim()).filter(Boolean);
      return items.length > 0 ? { kind: 'tags', items } : { kind: 'empty' };
    }
    case 'rating': {
      const match = RATING.exec(raw);
      const [value, max] = match ? [toNumber(match[1]!), toNumber(match[2]!)] : [null, null];
      return value !== null && max !== null && max > 0 && value >= 0 && value <= max
        ? { kind: 'rating', value, max, text: `${fr(value, 1)}/${fr(max, 1)}` }
        : { kind: 'text', text: raw };
    }
    case 'decision': return { kind: 'decision', text: raw };
    case 'link': return linkOf(raw);
    case 'email': return EMAIL.test(raw) ? { kind: 'email', href: `mailto:${raw}`, text: raw } : { kind: 'text', text: raw };
    case 'long_text': return { kind: 'long_text', text: raw };
    default: return { kind: 'text', text: raw };
  }
};

/** Formats that line up on the right in a list. */
export const isFigure = (format: CellFormat) => format === 'number' || format === 'money' || format === 'percent' || format === 'rating';

/** The initials of an object's name, for its badge: « Boréal Services » → « BS ». */
export const initialsOf = (name: string): string => {
  const words = name.replace(/[^\p{L}\p{N}\s'-]/gu, ' ').split(/[\s'-]+/).filter(Boolean);
  const letters = (words.length > 1 ? [words[0]!, words[1]!] : words.slice(0, 1)).map((word) => word.slice(0, words.length > 1 ? 1 : 2));
  return letters.join('').toUpperCase();
};
